import { useCallback, useEffect, useState } from 'react';
import { VideoTrackTimelineStore } from 'modules/VideoTrackTimeline/stores';
import { useRafState } from 'react-use';

type ConfigType = {
  currentFrame: number;
  videoElement: HTMLVideoElement;
  store: VideoTrackTimelineStore;
  fps: number;
  framesCount: number;
  onCurrentFrameChanged: (frame: number, fps: number) => void;
};

type ReturnType = {
  onPlay: () => void;
  onPause: () => void;
  prevFrame: (frame: number) => void;
  nextFrame: (frame: number) => void;
  isPlaying: boolean;
  isMuted: boolean;
  changeMute: (mute: boolean) => void;
  changeCurrentFrame: (time: number) => void;
  pauseWithAdjust: (time: number) => void;
};

export const useVideoControllers = ({ currentFrame, videoElement, store, fps, framesCount, onCurrentFrameChanged }: ConfigType): ReturnType => {
  const [isMuted, setIsMuted] = useState(false);
  const [rafThrottledFrame, setThrottledFrame] = useRafState(currentFrame);

  const changeCurrentFrame = useCallback(
    (frame: number = 0) => {
      const newTime = frame / fps;
      videoElement.currentTime = newTime;
      onCurrentFrameChanged(frame, fps);
    },
    [fps, onCurrentFrameChanged, videoElement],
  );

  const pauseWithAdjust = useCallback(
    (frame: number) => {
      videoElement.pause();
      const adjustedFrame = Math.min(framesCount, frame);
      changeCurrentFrame(adjustedFrame);
      store.setPlayingFrame(adjustedFrame);
      store.setIsVideoPlaying(false);
    },
    [videoElement, framesCount, changeCurrentFrame],
  );

  const onPlay = useCallback(() => {
    videoElement.play();
    videoElement.currentTime = store.getPlayingTime() / fps;
    store.setIsVideoPlaying(true);
  }, [videoElement, store.getPlayingTime()]);

  const onPause = useCallback(() => {
    pauseWithAdjust(currentFrame);
  }, [pauseWithAdjust, currentFrame]);

  const changeMute = useCallback(
    (mute: boolean) => {
      videoElement.muted = mute;

      setIsMuted(mute);
    },
    [videoElement, isMuted],
  );

  const prevFrame = useCallback(
    (step: number) => {
      pauseWithAdjust(Math.max(0, currentFrame - step));
    },
    [pauseWithAdjust, currentFrame],
  );

  const nextFrame = useCallback(
    (step: number) => {
      pauseWithAdjust(Math.min(framesCount, currentFrame + step));
    },
    [pauseWithAdjust, currentFrame, framesCount],
  );

  const onEnded = useCallback(() => {
    store.setIsVideoPlaying(false);
  }, []);

  useEffect(() => {
    onCurrentFrameChanged(rafThrottledFrame, fps);
  }, [onCurrentFrameChanged, rafThrottledFrame]);

  const onTimeUpdate = useCallback(() => {
    const frameNumber = Math.round(videoElement.currentTime * fps);
    setThrottledFrame(Math.min(frameNumber, framesCount));
  }, [videoElement.currentTime, fps]);

  useEffect(() => {
    videoElement.addEventListener('ended', onEnded);
    videoElement.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      videoElement.removeEventListener('ended', onEnded);
      videoElement.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [videoElement]);

  return {
    onPause,
    onPlay,
    isPlaying: store.getIsVideoPlaying(),
    changeMute,
    isMuted,
    changeCurrentFrame,
    prevFrame,
    nextFrame,
    pauseWithAdjust,
  };
};
